// src/pages/hr/learning/QuizTab.jsx
// ============================================================
// Learning & Development - Quiz Evaluation
// Redesigned with Green Theme & Outline Icons
// ============================================================

import React, { useState } from "react";
import {
  CheckCircle,
  XCircle,
  AlertTriangle,
  Award,
  Lock,
  RefreshCw,
  CheckCircle2,
} from "lucide-react";

export default function QuizTab({
  learningState,
  currentUser,
}) {
  const { data, submitQuizAttempt } = learningState;
  const courses = data?.courses || [];
  const progressRecords = data?.progressRecords || [];

  const [selectedCourseId, setSelectedCourseId] = useState(null);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);

  const empId = currentUser?.uid || currentUser?.id || "";

  // Seed display courses if catalog is empty
  const displayCourses = courses.length > 0 ? courses : [
    {
      id: "seed-crs-1",
      title: "Cybersecurity & Data Privacy Essentials 2026",
      category: "Compliance & Security",
      quiz: {
        passPercentage: 70,
        questions: [
          {
            id: "q1",
            question: "What should you do if you receive a suspicious email link?",
            options: ["Report to IT Security immediately", "Click the link to verify", "Forward to all colleagues", "Delete and ignore"],
            correctAnswer: 0,
          },
          {
            id: "q2",
            question: "What is the minimum recommended length for corporate passwords?",
            options: ["6 characters", "12+ characters with special symbols", "4 numbers", "Firstname123"],
            correctAnswer: 1,
          },
        ],
      },
    },
  ];

  // Filter courses where video is completed but quiz not yet attempted
  const availableForQuiz = displayCourses.filter((c) => {
    const progress = progressRecords.find((p) => p.courseId === c.id && p.employeeId === empId);
    return progress?.videoCompleted || false;
  });

  const lockedCourses = displayCourses.filter((c) => {
    const progress = progressRecords.find((p) => p.courseId === c.id && p.employeeId === empId);
    return !progress?.videoCompleted;
  });

  const selectedCourse = displayCourses.find((c) => c.id === selectedCourseId);
  const quiz = selectedCourse?.quiz;

  const handleSelectOption = (questionIdx, optionIdx) => {
    if (result) return;
    setAnswers({ ...answers, [questionIdx]: optionIdx });
  };

  const handleSubmit = () => {
    if (!quiz || !selectedCourse) return;
    const quizResult = submitQuizAttempt(
      selectedCourse.id,
      currentUser,
      answers,
      quiz,
      currentUser
    );
    setResult(quizResult);
  };

  const handleReset = () => {
    setSelectedCourseId(null);
    setAnswers({});
    setResult(null);
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "1.25rem" }}>
      <style>{`
        .lnd-green-badge {
          background: #f0fdf4;
          color: #15803d;
          border: 1.5px solid #bbf7d0;
          padding: 0.2rem 0.6rem;
          border-radius: 99px;
          font-size: 0.72rem;
          font-weight: 700;
          display: inline-flex;
          align-items: center;
          gap: 0.25rem;
        }
        .btn-green-primary {
          background: #1b4b31;
          color: #ffffff;
          border: none;
          border-radius: 9px;
          padding: 0.48rem 0.95rem;
          font-weight: 700;
          font-size: 0.82rem;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          gap: 0.4rem;
          transition: all 0.18s ease;
        }
        .btn-green-primary:hover {
          background: #143724;
        }
        .btn-outline-action {
          background: #ffffff;
          border: 1.5px solid #cbd5e1;
          border-radius: 9px;
          padding: 0.45rem 0.85rem;
          font-weight: 700;
          font-size: 0.78rem;
          color: #475569;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          gap: 0.35rem;
          transition: all 0.18s ease;
        }
        .btn-outline-action:hover {
          background: #f8fafc;
          border-color: #94a3b8;
        }
      `}</style>

      {/* If quiz is active (selectedCourseId is set), show quiz view */}
      {selectedCourseId && quiz ? (
        <div className="card" style={{ padding: "1.75rem", maxWidth: "760px", background: "#ffffff", borderRadius: 16, border: "1px solid #e2e8f0" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1.25rem", borderBottom: "1px solid #f1f5f9", paddingBottom: "1rem" }}>
            <div>
              <h3 style={{ margin: 0, fontSize: "1.1rem", fontWeight: 800, color: "#0f172a" }}>{selectedCourse.title}</h3>
              <p style={{ margin: "0.25rem 0 0 0", fontSize: "0.8rem", color: "#64748b" }}>
                {quiz.questions.length} Question{quiz.questions.length > 1 ? "s" : ""} • Pass Mark: {quiz.passPercentage}%
              </p>
            </div>
            <button className="btn-outline-action" onClick={handleReset}>
              <RefreshCw size={14} strokeWidth={1.8} /> Back to Courses
            </button>
          </div>

          {/* Quiz Questions */}
          {quiz.questions.map((q, qIdx) => (
            <div key={q.id || qIdx} style={{ marginBottom: "1.25rem", padding: "1.1rem", background: "#f8fafc", borderRadius: "12px", border: "1px solid #e2e8f0" }}>
              <p style={{ margin: "0 0 0.85rem 0", fontWeight: 800, fontSize: "0.9rem", color: "#0f172a" }}>
                Q{qIdx + 1}. {q.question}
              </p>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.6rem" }}>
                {q.options.map((opt, oIdx) => {
                  const isSelected = answers[qIdx] === oIdx;
                  const isCorrect = result && q.correctAnswer === oIdx;
                  const isWrong = result && isSelected && q.correctAnswer !== oIdx;

                  let borderColor = "#cbd5e1";
                  let bg = "#ffffff";
                  let textColor = "#334155";
                  if (isSelected && !result) { borderColor = "#15803d"; bg = "#f0fdf4"; textColor = "#15803d"; }
                  if (isCorrect) { borderColor = "#15803d"; bg = "#f0fdf4"; textColor = "#15803d"; }
                  if (isWrong) { borderColor = "#dc2626"; bg = "#fef2f2"; textColor = "#dc2626"; }

                  return (
                    <button
                      key={oIdx}
                      onClick={() => handleSelectOption(qIdx, oIdx)}
                      disabled={!!result}
                      style={{
                        padding: "0.65rem 0.85rem",
                        borderRadius: "8px",
                        border: `1.5px solid ${borderColor}`,
                        background: bg,
                        color: textColor,
                        cursor: result ? "default" : "pointer",
                        textAlign: "left",
                        fontSize: "0.83rem",
                        fontWeight: isSelected ? 700 : 500,
                        display: "flex",
                        alignItems: "center",
                        gap: "0.5rem",
                        transition: "all 0.15s ease",
                      }}
                    >
                      {isCorrect && <CheckCircle size={14} strokeWidth={2} color="#15803d" />}
                      {isWrong && <XCircle size={14} strokeWidth={2} color="#dc2626" />}
                      <span style={{ fontWeight: 800, marginRight: "4px" }}>{String.fromCharCode(65 + oIdx)}.</span> {opt}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}

          {/* Submit / Result */}
          {!result ? (
            <button
              className="btn-green-primary"
              onClick={handleSubmit}
              disabled={Object.keys(answers).length < quiz.questions.length}
              style={{ padding: "0.6rem 1.5rem", fontSize: "0.88rem", opacity: Object.keys(answers).length < quiz.questions.length ? 0.5 : 1 }}
            >
              Submit Quiz Assessment
            </button>
          ) : (
            <div style={{
              padding: "1.1rem 1.35rem",
              borderRadius: "12px",
              background: result.passed ? "#f0fdf4" : "#fef2f2",
              border: `1.5px solid ${result.passed ? "#bbf7d0" : "#fca5a5"}`,
              display: "flex",
              alignItems: "center",
              gap: "0.85rem",
            }}>
              {result.passed ? <Award size={26} strokeWidth={1.8} color="#15803d" /> : <AlertTriangle size={26} strokeWidth={1.8} color="#dc2626" />}
              <div>
                <div style={{ fontWeight: 800, fontSize: "1.05rem", color: result.passed ? "#143724" : "#991b1b" }}>
                  {result.passed ? "Congratulations! You Passed!" : "Quiz Evaluation Failed"}
                </div>
                <div style={{ fontSize: "0.82rem", color: "#64748b", marginTop: "2px" }}>
                  Score: <strong style={{ color: "#0f172a" }}>{result.scorePct}%</strong> ({result.correctCount}/{result.totalQuestions} correct)
                </div>
              </div>
            </div>
          )}
        </div>
      ) : (
        /* Course Selection View */
        <>
          {/* Available for Quiz */}
          {availableForQuiz.length > 0 && (
            <div className="card" style={{ padding: "1.35rem", background: "#ffffff", borderRadius: 14, border: "1px solid #e2e8f0" }}>
              <h3 style={{ margin: "0 0 1rem 0", fontSize: "1rem", fontWeight: 800, display: "flex", alignItems: "center", gap: "0.55rem", color: "#0f172a" }}>
                <CheckCircle2 size={18} strokeWidth={1.8} color="#15803d" /> Ready to Take Quiz ({availableForQuiz.length})
              </h3>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: "1rem" }}>
                {availableForQuiz.map((c) => {
                  const progress = progressRecords.find((p) => p.courseId === c.id && p.employeeId === empId);
                  const alreadyDone = progress?.quizAttempted;
                  return (
                    <div key={c.id} style={{ padding: "1.1rem", borderRadius: "10px", border: "1px solid #e2e8f0", background: "#f8fafc" }}>
                      <div style={{ marginBottom: "0.5rem" }}>
                        <span className="lnd-green-badge">{c.category}</span>
                      </div>
                      <h4 style={{ margin: "0 0 0.5rem 0", fontSize: "0.95rem", fontWeight: 800, color: "#0f172a" }}>{c.title}</h4>
                      <p style={{ margin: "0 0 0.85rem 0", fontSize: "0.8rem", color: "#64748b" }}>
                        {c.quiz?.questions?.length || 0} Questions • Pass Mark: {c.quiz?.passPercentage || 70}%
                      </p>
                      {alreadyDone ? (
                        <div className="lnd-green-badge">
                          {progress.passed ? "Passed" : "Failed"} — Score: {progress.quizScore}%
                        </div>
                      ) : (
                        <button
                          className="btn-green-primary"
                          onClick={() => { setSelectedCourseId(c.id); setAnswers({}); setResult(null); }}
                        >
                          <Award size={14} strokeWidth={1.8} /> Start Quiz
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Locked Courses */}
          {lockedCourses.length > 0 && (
            <div className="card" style={{ padding: "1.35rem", background: "#ffffff", borderRadius: 14, border: "1px solid #e2e8f0" }}>
              <h3 style={{ margin: "0 0 1rem 0", fontSize: "1rem", fontWeight: 800, display: "flex", alignItems: "center", gap: "0.55rem", color: "#64748b" }}>
                <Lock size={18} strokeWidth={1.8} /> Quiz Locked ({lockedCourses.length})
              </h3>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: "1rem" }}>
                {lockedCourses.map((c) => (
                  <div key={c.id} style={{ padding: "1.1rem", borderRadius: "10px", border: "1.5px dashed #cbd5e1", background: "#f8fafc", opacity: 0.8 }}>
                    <h4 style={{ margin: "0 0 0.35rem 0", fontSize: "0.92rem", fontWeight: 700, color: "#475569" }}>{c.title}</h4>
                    <p style={{ margin: 0, fontSize: "0.78rem", color: "#64748b", display: "flex", alignItems: "center", gap: "4px" }}>
                      <Lock size={12} strokeWidth={1.8} /> Complete the video training first to unlock this quiz.
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {availableForQuiz.length === 0 && lockedCourses.length === 0 && (
            <div className="card" style={{ padding: "3rem", textAlign: "center", color: "#64748b", background: "#ffffff", borderRadius: 14, border: "1px solid #e2e8f0" }}>
              <Award size={40} strokeWidth={1.5} color="#94a3b8" style={{ margin: "0 auto 0.5rem" }} />
              <p style={{ margin: 0, fontWeight: 600 }}>No courses available. Create courses in the Courses tab first.</p>
            </div>
          )}
        </>
      )}
    </div>
  );
}
